import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { FaSearch, FaTimes, FaSpinner, FaPlus, FaTrash, FaUndo } from 'react-icons/fa';
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';
const token = localStorage.getItem('authToken');

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0
  }).format(amount);
}

const FeaturedProductsPage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [addingId, setAddingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const searchTimeout = useRef(null);

  // Fetch all featured products
  const fetchFeaturedProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/featured-products`);
      setFeaturedProducts(response.data || []);
    } catch (error) {
      setMessage('Failed to fetch featured products.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  // Debounced search
  const handleSearchDebounced = (value) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => handleSearch(value), 400);
  };

  // Search for products to add as featured
  const handleSearch = async (value = searchTerm) => {
    if (!value) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/product-list?search=${encodeURIComponent(value)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Exclude products already in featured collection
      const filtered = response.data.filter(prod =>
        !featuredProducts.some(f => f._id === prod._id)
      );
      setSearchResults(filtered);
    } catch (error) {
      setMessage('Failed to search products.');
    } finally {
      setSearching(false);
    }
  };

  // Add a product to the featured collection
  const handleAddProduct = async (prod) => {
    setAddingId(prod._id);
    try {
      // Only send productId - all data comes from live product collection
      const payload = {
        productId: prod._id,
      };
      const response = await axios.post(`${API_BASE_URL}/featured-product`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(response.data.message || 'Product added.');
      fetchFeaturedProducts();
      setSearchResults([]);
      setSearchTerm('');
    } catch (error) {
      setMessage('Failed to add product.');
    } finally {
      setAddingId(null);
    }
  };

  // Remove a featured product with confirmation
  const handleRemoveProduct = async (featuredId) => {
    if (!window.confirm('Are you sure you want to remove this product from featured products?')) return;
    setRemovingId(featuredId);
    try {
      const response = await axios.delete(`${API_BASE_URL}/featured-product/${featuredId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(response.data.message || 'Product removed.');
      fetchFeaturedProducts();
    } catch (error) {
      setMessage('Failed to remove product.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setMessage('');
  };

  return (
    <div className="container-fluid p-3 pt-2 g-0 adma">
      {/* Heading */}
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div className="section-title">
              <h4 className="mt-1">TOP PICKS ({featuredProducts.length} TOTAL)</h4>
              <p className="text-muted p-0 m-0">Add existing products to your featured product list</p>
            </div>
          </div>
        </div>
      </div>

      {/* Control Panel — cloned from Products/Customers pages */}
      <div className="control-panel-bar">
        <div className="control-panel-search">
          <span
            className="position-absolute top-50 translate-middle-y start-0 ps-3 text-muted"
            style={{ pointerEvents: 'none' }}
          >
            <FaSearch style={{ fontSize: '14px' }} />
          </span>
          <input
            type="text"
            className="form-control form-control-sm ps-5"
            placeholder="Search by product name, brand, or category to add..."
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              height: '38px',
              fontSize: '14px',
              background: '#ffffff',
              width: '100%'
            }}
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              handleSearchDebounced(e.target.value);
            }}
          />
          {searchTerm && (
            <button
              className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0"
              style={{ zIndex: 2, border: 'none', background: 'none', color: '#94a3b8' }}
              onClick={handleClearSearch}
              tabIndex={-1}
            >
              <FaTimes />
            </button>
          )}
        </div>
        <button
          type="button"
          className="btn-reset-square"
          title="Reset Search"
          onClick={handleClearSearch}
          style={{ height: '38px', width: '38px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <FaUndo style={{ fontSize: '14px' }} />
        </button>
        <div className="control-panel-reset" style={{ marginLeft: 'auto' }}>
          <small className="text-muted" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
            Showing {featuredProducts.length} featured products
          </small>
        </div>
      </div>

      {/* Search Results Table */}
      {searchResults.length > 0 && (
        <div className="row orders-table-row mt-3">
          <div className="col-12">
            <div className="card-header py-2 px-3" style={{ background: '#e8eaf6', border: '1px solid #d1d5db', borderBottom: 'none' }}>
              <strong style={{ color: '#1a237e', fontSize: '13px', textTransform: 'uppercase' }}>
                Search Results ({searchResults.length} Found)
              </strong>
            </div>
            <div className="table-responsive" style={{ border: '1px solid #d1d5db' }}>
              {searching ? (
                <div className="text-center my-4 py-3 bg-white">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="ms-2 text-muted" style={{ fontSize: '14px' }}>Searching products...</span>
                </div>
              ) : (
                <table className="table table-hover orders-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '8%', minWidth: '50px' }}>#</th>
                      <th style={{ width: '37%', minWidth: '220px' }}>Product Info</th>
                      <th style={{ width: '25%', minWidth: '180px' }}>Category & Brand</th>
                      <th style={{ width: '12%', minWidth: '100px' }}>Price</th>
                      <th style={{ width: '10%', minWidth: '100px' }}>Quantity</th>
                      <th style={{ width: '8%', minWidth: '80px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((prod, idx) => (
                      <tr key={prod._id}>
                        <td>
                          <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                            {idx + 1}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div style={{ width: '40px', height: '40px', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                              <img 
                                src={`${API_BASE_URL}/${prod.thumbnail}`} 
                                alt={prod.name} 
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/40?text=P';
                                }}
                              />
                            </div>
                            <div className="d-flex flex-column align-items-start">
                              <strong style={{ color: '#1a237e', fontSize: '14px' }}>{prod.name}</strong>
                              {prod.description && (
                                <span className="text-muted text-start" style={{ fontSize: '11px', marginTop: '2px', display: 'block', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {prod.description}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                              <span className="text-muted" style={{ fontWeight: '500' }}>Category:</span>
                              <span style={{ fontWeight: '600', color: '#0f172a' }}>{prod.category || 'N/A'}</span>
                            </div>
                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                              <span className="text-muted" style={{ fontWeight: '500' }}>Brand:</span>
                              <span style={{ fontWeight: '600', color: '#92400e' }}>{prod.brand || 'N/A'}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start">
                            <span style={{ fontWeight: '700', color: '#16a34a', fontSize: '15px' }}>
                              {formatCurrency(prod.price)}
                            </span>
                            {prod.originalPrice && (
                              <span className="text-muted" style={{ fontSize: '11px', textDecoration: 'line-through', marginTop: '1px' }}>
                                MRP: {formatCurrency(prod.originalPrice)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start">
                            <span className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                              {prod.quantity} {prod.unit}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn-action-square btn-action-accept"
                              title="Add to Featured"
                              onClick={() => handleAddProduct(prod)}
                              disabled={addingId === prod._id}
                            >
                              {addingId === prod._id ? (
                                <FaSpinner className="fa-spin" size={12} />
                              ) : (
                                <FaPlus size={12} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Featured Products Table */}
      {loading ? (
        <AdminPageLoader message="Loading featured products..." />
      ) : (
        <div className="row orders-table-row mt-4">
          <div className="col-12">
            <div className="card-header py-2 px-3" style={{ background: '#e8eaf6', border: '1px solid #d1d5db', borderBottom: 'none' }}>
              <strong style={{ color: '#1a237e', fontSize: '13px', textTransform: 'uppercase' }}>
                FEATURED PRODUCTS COLLECTION
              </strong>
            </div>
            <div className="table-responsive" style={{ border: '1px solid #d1d5db' }}>
              <table className="table table-hover orders-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '8%', minWidth: '50px' }}>#</th>
                    <th style={{ width: '37%', minWidth: '220px' }}>Product Info</th>
                    <th style={{ width: '25%', minWidth: '180px' }}>Category & Brand</th>
                    <th style={{ width: '12%', minWidth: '100px' }}>Price</th>
                    <th style={{ width: '10%', minWidth: '100px' }}>Quantity</th>
                    <th style={{ width: '8%', minWidth: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {featuredProducts.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted bg-white" style={{ fontSize: '14px' }}>
                        No featured products added yet. Use the search bar to find and add products.
                      </td>
                    </tr>
                  ) : (
                    featuredProducts.map((prod, idx) => (
                      <tr key={prod._id}>
                        <td>
                          <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                            {idx + 1}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div style={{ width: '40px', height: '40px', background: '#f1f5f9', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                              <img 
                                src={`${API_BASE_URL}/${prod.thumbnail}`} 
                                alt={prod.name} 
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/40?text=P';
                                }}
                              />
                            </div>
                            <div className="d-flex flex-column align-items-start">
                              <strong style={{ color: '#1a237e', fontSize: '14px' }}>{prod.name}</strong>
                              {prod.description && (
                                <span className="text-muted text-start" style={{ fontSize: '11px', marginTop: '2px', display: 'block', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {prod.description}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                              <span className="text-muted" style={{ fontWeight: '500' }}>Category:</span>
                              <span style={{ fontWeight: '600', color: '#0f172a' }}>{prod.category || 'N/A'}</span>
                            </div>
                            <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                              <span className="text-muted" style={{ fontWeight: '500' }}>Brand:</span>
                              <span style={{ fontWeight: '600', color: '#92400e' }}>{prod.brand || 'N/A'}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start">
                            <span style={{ fontWeight: '700', color: '#16a34a', fontSize: '15px' }}>
                              {formatCurrency(prod.price)}
                            </span>
                            {prod.originalPrice && (
                              <span className="text-muted" style={{ fontSize: '11px', textDecoration: 'line-through', marginTop: '1px' }}>
                                MRP: {formatCurrency(prod.originalPrice)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start">
                            <span className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                              {prod.quantity} {prod.unit}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn-action-square btn-action-cancel"
                              title="Remove from Featured"
                              onClick={() => handleRemoveProduct(prod.featuredId)}
                              disabled={removingId === prod.featuredId}
                            >
                              {removingId === prod.featuredId ? (
                                <FaSpinner className="fa-spin" size={12} />
                              ) : (
                                <FaTrash size={12} />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {message && <div className="alert alert-info mt-3 mb-0 py-2 px-3">{message}</div>}
    </div>
  );
};

export default FeaturedProductsPage;